import { normalize } from 'normalizr';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import has from 'lodash/has';
import isNil from 'lodash/isNil';
import replace from 'lodash/replace';

import * as actionTypes from '../../constants/actionTypes';
import { setRequestInProcess } from '../request';
import { getAllTags } from '../tags';
import { loadVendors } from '../vendors';

import { CALL_AUTH_API, CALL_PRIVATE_API } from '../../middleware/api';
import { sessionSchema } from '../../schemas';
import { GET, POST } from '../../constants/requestMethods';
import { getAccessToken, getCurrentUser } from '../../reducers/selectors';

import { isValidEmail } from '../../lib/ValidationUtils';
import { beginIdpAuth, fetchIdpToken } from '../../services/SSOAuthService';
import GoogleAuthService, { G_PROFILE_KEY } from '../../services/GoogleAuthService';
import LocalStorageUtils from '../../lib/LocalStorageUtils';
import AnalyticsUtils from '../../lib/AnalyticsUtils';
import { SSO_AUTH_KEYS, SSO_AUTH_TYPES } from '../../constants/auth';

import { API_SERVER_URL } from '../../constants/api';
import { GOOGLE_SSO_ENABLED } from '../../constants/config';
import { MAIN_ALL } from '../../constants/notificationsTypes';
import { parseJwt } from '../../../js/services/utils/parseJwt';
import { getUserSettings, loadUser } from '../users';
import { loadAllNotifications, loadUnreadNotifications } from '../notifications';
import { fetchCurrentOrg } from '../orgs';
import { ACCESS_CONTROL_LOCALSTORAGE_KEY } from 'js/reducers/app/accessControlReducer';

export const getGProfile = (profile) => {
  return {
    full_name: profile.given_name + ' ' + profile.family_name,
    getImageUrl() {
      return profile.picture;
    },
    email: profile.email,
  };
};

export const getSessionHistory = (id) => (dispatch) => {
  dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.SESSION_HISTORY_REQUEST,
        actionTypes.SESSION_HISTORY_SUCCESS,
        actionTypes.SESSION_HISTORY_FAILURE,
      ],
      endpoint: `/users/${id}/login_history`,
      requestMethod: GET,
    },
  });
};

export const clearSessionError = () => (dispatch) => {
  return dispatch({
    type: actionTypes.CLEAR_SESSION_ERROR,
    payload: {},
  });
};

const authWithUsername = (username, password) => (dispatch) => {
  dispatch(clearSessionError());
  dispatch({
    [CALL_AUTH_API]: {
      types: [
        actionTypes.NEX_USERNAME_AUTH_REQUEST,
        actionTypes.NEX_USERNAME_AUTH_SUCCESS,
        actionTypes.NEX_USERNAME_AUTH_FAILURE,
      ],
      endpoint: `/token`,
      schema: sessionSchema,
      requestMethod: POST,
      payload: {
        username,
        password,
      },
      ignoreErrors: true,
    },
  }).then(() => loadInitialUserData(dispatch));
};

const verifyEmailAction = (token) => (dispatch) => {
  return dispatch({
    [CALL_AUTH_API]: {
      types: [
        actionTypes.NEX_VERIFY_EMAIL_REQUEST,
        actionTypes.NEX_VERIFY_EMAIL_SUCCESS,
        actionTypes.NEX_VERIFY_EMAIL_FAILURE,
      ],
      endpoint: `/signup/verify_email?token=${token}`,
      requestMethod: GET,
      ignoreErrors: true,
    },
  });
};

const signUpAction = (payload) => (dispatch) => {
  dispatch(clearSessionError());
  return dispatch({
    [CALL_AUTH_API]: {
      types: [
        actionTypes.NEX_SIGNUP_REQUEST,
        actionTypes.NEX_SIGNUP_SUCCESS,
        actionTypes.NEX_SIGNUP_FAILURE,
      ],
      endpoint: `/signup`,
      requestMethod: POST,
      payload,
      ignoreErrors: true,
    },
  });
};

const authWithGtoken = (gToken, isSignup) => (dispatch) => {
  dispatch(clearSessionError());
  dispatch({
    [CALL_AUTH_API]: {
      types: [
        actionTypes.NEX_GTOKEN_AUTH_REQUEST,
        actionTypes.NEX_GTOKEN_AUTH_SUCCESS,
        actionTypes.NEX_GTOKEN_AUTH_FAILURE,
      ],
      endpoint: `/gtoken`,
      schema: sessionSchema,
      requestMethod: POST,
      payload: {
        gToken,
        ...(isSignup ? { self_signup: true } : {}),
      },
      ignoreErrors: true,
    },
  }).then(() => loadInitialUserData(dispatch));
};

const reAuthWithToken = (accessToken, isGoogleUser) => ({
  [CALL_AUTH_API]: {
    types: [
      actionTypes.NEX_REFRESH_AUTH_REQUEST,
      actionTypes.NEX_REFRESH_AUTH_SUCCESS,
      actionTypes.NEX_REFRESH_AUTH_FAILURE,
    ],
    endpoint: `/token/refresh`,
    schema: sessionSchema,
    requestMethod: POST,
    payload: {
      accessToken,
      isGoogleUser,
    },
  },
});

const gApiLogoutComplete =
  (error = null) =>
  (dispatch) => {
    dispatch(setRequestInProcess(false, actionTypes.GAPI_LOGOUT_REQUEST));
    if (!error) {
      return dispatch({
        type: actionTypes.GAPI_LOGOUT_SUCCESS,
      });
    } else {
      return dispatch({
        type: actionTypes.GAPI_LOGOUT_FAILURE,
        payload: {
          error,
        },
      });
    }
  };

const loadInitialUserData = (dispatch) => {
  dispatch(loadVendors());
  dispatch(getUserSettings(true));
  //TODO: NEX-13948: Temp hack for fetching org. We need it to know features enabled for the org

  dispatch(fetchCurrentOrg());
  dispatch(getAllTags(true));
  dispatch(loadAllNotifications(MAIN_ALL, 20, 0, 1));
};

export const logoutFromGoogle = () => (dispatch, getState) => {
  dispatch(setRequestInProcess(true, actionTypes.GAPI_LOGOUT_REQUEST));

  return dispatch(gApiLogoutComplete());
};

export const verifyEmail =
  ({ token }) =>
  (dispatch) => {
    return dispatch(verifyEmailAction(token));
  };

export const signUp = (registerPayload) => (dispatch, getState) => {
  const { first_name, last_name, personal_info, ...rest } = registerPayload;

  const full_name = first_name + ' ' + last_name;
  const lead_info = { ...personal_info, first_name, last_name };

  if (!registerPayload || !registerPayload?.email || !isValidEmail(registerPayload.email)) {
    return dispatch({
      type: actionTypes.NEX_USERNAME_AUTH_FAILURE,
      error: 'Please enter a valid email address',
      receivedAt: Date.now(),
    });
  }

  return dispatch(signUpAction({ ...rest, personal_info: lead_info, full_name }));
};

export const loginWithUsername = (username, password) => (dispatch) => {
  if (!password || !username) {
    return dispatch({
      type: actionTypes.NEX_USERNAME_AUTH_FAILURE,
      error: 'Please enter a valid email address and password',
      receivedAt: Date.now(),
    });
  } else if (!isValidEmail(username)) {
    return dispatch({
      type: actionTypes.NEX_USERNAME_AUTH_FAILURE,
      error: 'Please enter a valid email address',
      receivedAt: Date.now(),
    });
  } else {
    return dispatch(authWithUsername(username, password));
  }
};

export const loginWithGtoken = (gAuthToken) => (dispatch, getState) => {
  return dispatch(authWithGtoken(gAuthToken));
};

export const signupWithGtoken = (gAuthToken) => (dispatch, getState) => {
  return dispatch(authWithGtoken(gAuthToken, true));
};

export const logout =
  (shouldRevokeToken = true) =>
  (dispatch, getState) => {
    //request to revoke session token should only be sent if there is a session token.
    if (shouldRevokeToken === false) {
      return dispatch({
        type: actionTypes.LOGOUT_COMPLETE,
      });
    }

    const accessToken = getAccessToken(getState());

    if (!isNil(accessToken)) {
      dispatch({
        [CALL_PRIVATE_API]: {
          types: [
            actionTypes.NEX_LOGOUT_REQUEST,
            actionTypes.NEX_LOGOUT_SUCCESS,
            actionTypes.NEX_LOGOUT_FAILURE,
          ],
          endpoint: '/token/logout',
          requestMethod: POST,
        },
      });

      // Clear UI version cookie for dual-version routing
      fetch('/api/version/clear', {
        method: 'GET',
        credentials: 'include',
      }).catch(() => {
        // Silent fail - version cookie clearing is not critical
      });

      LocalStorageUtils.removeObject('nexSession');
      LocalStorageUtils.removeObject(`nex_org_id`);
      LocalStorageUtils.removeObject(`nex_org_name`);
      LocalStorageUtils.removeObject('nexla_section_navigation');
      // remove all conversation data stored for GenAi chat
      LocalStorageUtils.removeObjectWithPrefix('conversation_');
      AnalyticsUtils.clearUserSession();

      if (GOOGLE_SSO_ENABLED === true) {
        dispatch(logoutFromGoogle());
      }

      return dispatch({
        type: actionTypes.LOGOUT_COMPLETE,
      });
    }
  };

export const impersonate = (userId, orgId) => (dispatch, getState) => {
  dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.IMPERSONATION_REQUEST,
        actionTypes.IMPERSONATION_SUCCESS,
        actionTypes.IMPERSONATION_FAILURE,
      ],
      endpoint: '/token',
      schema: sessionSchema,
      requestMethod: POST,
      payload: {
        user_id: userId,
        org_id: orgId,
      },
    },
  }).then(() => {
    const state = getState();
    const {
      entities: { users, orgs },
      session: { impersonatedUser },
    } = state;

    const impersonatedUserId = impersonatedUser.user;
    const impersonatedOrgId = impersonatedUser.org;

    const user = {
      ...users[impersonatedUserId],
      //default_org: orgs[impersonatedUser.org]
    };
    const org = orgs[impersonatedOrgId];
    if (org) {
      LocalStorageUtils.saveObject(`nex_org_id`, org.id);
      LocalStorageUtils.saveObject(`nex_org_name`, org.name);
    }

    const normalized = normalize(
      {
        user,
        org,
        access_token: impersonatedUser.token,
        org_membership: { api_key: impersonatedUser.apiKey },
      },
      sessionSchema,
    );

    const localStorage = LocalStorageUtils.getObject('nexSession');
    const updatedLocalStorage = merge(
      {
        impersonatedUser: impersonatedUserId,
        impersonatedOrg: impersonatedOrgId,
      },
      normalized,
      localStorage,
    );
    LocalStorageUtils.saveObject('nexSession', updatedLocalStorage);
    LocalStorageUtils.removeObject('nexla_section_navigation');
    // dispatch(getAllTags());
    window.location.reload();
  });
};

export const endImpersonation = () => (dispatch) => {
  // nexSession is always available, until unless user manually clear their session storage.
  const localStorage = LocalStorageUtils.getObject('nexSession');
  if (localStorage) {
    const impersonatedUser = localStorage.impersonatedUser;
    const omitList = has(localStorage, `entities.session[${impersonatedUser}]`)
      ? ['impersonatedUser', 'impersonatedOrg', `entities.session[${impersonatedUser}]`]
      : ['impersonatedUser', 'impersonatedOrg'];

    const updatedLocalStorage = omit(localStorage, omitList);
    LocalStorageUtils.saveObject('nexSession', updatedLocalStorage);
    LocalStorageUtils.removeObject('nex_org_id');
    LocalStorageUtils.removeObject('nex_org_name');
    LocalStorageUtils.removeObject('nexla_section_navigation');
  }
  dispatch({ type: actionTypes.END_IMPERSONATION });

  window.location.reload();
};

//Google Authorization
const gApiLoadComplete =
  (gAuthStatus, error = null) =>
  (dispatch) => {
    if (!error) {
      return dispatch({
        type: actionTypes.LOAD_GAPI_SUCCESS,
        payload: {
          gAuthStatus,
        },
      });
    } else {
      return dispatch({
        type: actionTypes.LOAD_GAPI_FAILURE,
        payload: {
          error,
        },
      });
    }
  };

const gApiLoginComplete =
  (response, error = null, mode) =>
  (dispatch) => {
    if (!error && response) {
      //Gapi login was successful. trigger Nexla Authentication
      let idToken = response && response.credential;
      const profile = parseJwt(idToken);
      const gProfile = getGProfile(profile);
      LocalStorageUtils.saveObject(G_PROFILE_KEY, profile);

      dispatch({
        type: mode === 'signup' ? actionTypes.GAPI_SIGNUP_SUCCESS : actionTypes.GAPI_LOGIN_SUCCESS,
        payload: {
          gProfile,
          gToken: idToken,
        },
      });
      dispatch(mode === 'signup' ? signupWithGtoken(idToken) : loginWithGtoken(idToken));
    } else {
      dispatch({
        type: mode === 'signup' ? actionTypes.GAPI_SIGNUP_FAILURE : actionTypes.GAPI_LOGIN_FAILURE,
        payload: {
          error,
        },
      });
    }
  };

export const initGoogleApi =
  (mode = 'signin', clientId) =>
  (dispatch, getState) => {
    dispatch({
      type: actionTypes.LOAD_GAPI,
    });
    GoogleAuthService.initGapi(
      () => {
        const { isGAuthLoggedIn, gAuthUser, gToken } = GoogleAuthService.getAuthStatus();
        if (isGAuthLoggedIn) {
          dispatch(gApiLoadComplete({ gProfile: gAuthUser, gToken, isGAuthLoggedIn }));
        }
      },
      (response) => {
        return dispatch(gApiLoginComplete(response, null, mode));
      },
      mode,
      clientId,
    );
  };

export const startSessionFromLocalStore = () => {
  let nexSession = LocalStorageUtils.getObject('nexSession');

  //get user from session and trigger analytics call
  if (
    has(nexSession, 'entities') &&
    has(nexSession, 'result') &&
    !isNil(nexSession.entities) &&
    !isNil(nexSession.result)
  ) {
    let entities = nexSession.entities;
    let result = nexSession.result;
    let session = has(entities, `session[${result}]`) ? entities.session[result] : null;

    if (session) {
      const thisUser =
        has(session, 'user') && has(entities, `users[${session.user}]`)
          ? entities.users[session.user]
          : null;
      const userOrgId = has(session, 'org') ? session.org : null;

      const thisOrg =
        userOrgId && has(entities, `orgs[${userOrgId}]`) ? entities.orgs[userOrgId] : null;

      if (!isNil(thisUser) && !isNil(userOrgId)) {
        // console.log('sending to utils');
        AnalyticsUtils.registerUserSession(thisUser, userOrgId, thisOrg);
      }
    }
  }

  // Hydrate section navigation from localStorage
  const sectionNavigation = LocalStorageUtils.getObject('nexla_section_navigation') || {};

  return {
    type: actionTypes.HYDRATE_FROM_LOCAL_STORE,
    ...nexSession,
    sectionNavigation,
  };
};

export function startAccessControlFromLocalStore() {
  const level = LocalStorageUtils.getObject(ACCESS_CONTROL_LOCALSTORAGE_KEY);

  return {
    mode: level || undefined,
    type: actionTypes.HYDRATE_FROM_LOCAL_STORE,
  };
}

export const refreshNexlaAccessToken = (accessToken, isGoogleUser) => (dispatch) => {
  return dispatch(reAuthWithToken(accessToken, isGoogleUser));
};

//SAML support

export const authWithSSO =
  (ssoAuthInfo = {}) =>
  (dispatch) => {
    const type = ssoAuthInfo.type;
    let tokenParams = {};
    let hasTokenInfo = false;
    let endpoint = '/sso_token';
    const nexlaTokenUrl = ssoAuthInfo[SSO_AUTH_KEYS.nexla_token_url];

    switch (type) {
      case SSO_AUTH_TYPES.saml:
        if (has(ssoAuthInfo, 'saml_token') && !isNil(ssoAuthInfo.saml_token)) {
          endpoint = replace(nexlaTokenUrl, API_SERVER_URL, '');
          hasTokenInfo = true;
          tokenParams['SAMLResponse'] = ssoAuthInfo.saml_token;
        } else if (has(ssoAuthInfo, 'nex_token')) {
          endpoint = API_SERVER_URL + '/token/refresh';
          hasTokenInfo = true;
          tokenParams['accessToken'] = ssoAuthInfo.nex_token;
        }
        break;

      case SSO_AUTH_TYPES.oidc_pkce:
      case SSO_AUTH_TYPES.oidc_implicit:
        if (has(ssoAuthInfo, 'id_token') && !isNil(ssoAuthInfo.id_token)) {
          endpoint = replace(nexlaTokenUrl, API_SERVER_URL, '');
          hasTokenInfo = true;
          tokenParams['id_token'] = ssoAuthInfo.id_token;
          if (has(ssoAuthInfo, 'access_token') && !isNil(ssoAuthInfo.access_token)) {
            tokenParams['access_token'] = ssoAuthInfo.access_token;
          }
          if (ssoAuthInfo.self_signup) {
            tokenParams['self_signup'] = true;
          }
        }
        break;
      default:
        break;
    }

    if (!hasTokenInfo) {
      return dispatch({
        type: actionTypes.NEX_SSO_AUTH_LOGIN_FAILURE,
        error: 'Please enter valid user authentication information',
        receivedAt: Date.now(),
      });
    }

    dispatch(clearSessionError());
    return dispatch({
      [CALL_AUTH_API]: {
        types: [
          actionTypes.NEX_SSO_AUTH_LOGIN_REQUEST,
          actionTypes.NEX_SSO_AUTH_LOGIN_SUCCESS,
          actionTypes.NEX_SSO_AUTH_LOGIN_FAILURE,
        ],
        endpoint,
        requestMethod: POST,
        schema: sessionSchema,
        payload: tokenParams,
        ignoreErrors: true,
        requestInfo: ssoAuthInfo,
      },
    }).then(
      () => {
        dispatch(loadUser());
        loadInitialUserData(dispatch);
      },
      () => {
        LocalStorageUtils.removeObject('nexSession');
        LocalStorageUtils.removeObject('nexla_section_navigation');
        dispatch({ type: actionTypes.LOGOUT_COMPLETE });
      },
    );
  };

export const ssoAuthError =
  (errorMessage = null) =>
  (dispatch) => {
    LocalStorageUtils.removeObject('nexSession');
    LocalStorageUtils.removeObject('nexla_section_navigation');
    dispatch({ type: actionTypes.LOGOUT_COMPLETE });
    return dispatch({
      type: actionTypes.NEX_SSO_AUTH_LOGIN_FAILURE,
      error: errorMessage ? errorMessage : 'Please enter valid user authentication information',
      receivedAt: Date.now(),
    });
  };

export const ssoIdpAuthStart =
  (authInfo = {}) =>
  (dispatch) => {
    dispatch({
      type: actionTypes.SSO_IDP_AUTH_START,
      payload: {
        ...authInfo,
      },
    });

    dispatch(beginIdpAuthRedirect(authInfo));
  };

export const ssoIdpAuthReceiveCode =
  (codeInfo = {}) =>
  (dispatch, getState) => {
    dispatch({
      type: actionTypes.SSO_IDP_AUTH_RECEIVE_CODE,
      payload: {
        ...codeInfo,
      },
    });

    const {
      idpConfig: { configInfo, authParams, self_signup },
    } = getState();

    return fetchIdpToken(configInfo, authParams, codeInfo)
      .then((res) => {
        dispatch(ssoIdpAuthReceiveToken(res));

        return dispatch(
          authWithSSO({
            ...res,
            type: configInfo[SSO_AUTH_KEYS.authMode],
            [SSO_AUTH_KEYS.nexla_token_url]: configInfo[SSO_AUTH_KEYS.nexla_token_url],
            ...(self_signup ? { self_signup: true } : {}),
          }),
        );
      })
      .catch((err) => {
        return dispatch(ssoAuthError(err.message));
      });
  };

export const ssoIdpAuthReceiveToken = (tokenInfo = {}) => {
  return {
    type: actionTypes.SSO_IDP_AUTH_RECEIVE_TOKEN,
    payload: {
      ...tokenInfo,
    },
  };
};

export const beginIdpAuthRedirect =
  (idpAuthInfo = {}) =>
  (dispatch, getState) => {
    const localStorage = LocalStorageUtils.getObject('nexSession');
    const { session } = getState();
    // idpConfig must replace what's stored, not merge into it. Every attempt mints a
    // fresh nonce and PKCE verifier, and a stale one surviving here makes the state
    // check in SSOAuthLoadingView reject the IdP's callback.
    const updatedLocalStorage = {
      ...localStorage,
      idpConfig: idpAuthInfo,
      session: session,
    };
    LocalStorageUtils.saveObject('nexSession', updatedLocalStorage);

    const { configInfo, authParams } = idpAuthInfo;

    beginIdpAuth(configInfo, authParams);
  };

// mktplace
export const getMktplaceUser =
  (awsMktplaceToken = null) =>
  (dispatch) => {
    if (!awsMktplaceToken) {
      return dispatch({
        type: actionTypes.AWS_MKTPLACE_TOKEN_FAILURE,
        error: 'Could not find referrer token',
        receivedAt: Date.now(),
      });
    }

    return dispatch({
      [CALL_AUTH_API]: {
        types: [
          actionTypes.AWS_MKTPLACE_TOKEN_REQUEST,
          actionTypes.AWS_MKTPLACE_TOKEN_SUCCESS,
          actionTypes.AWS_MKTPLACE_TOKEN_FAILURE,
        ],
        endpoint: '/aws_marketplace_token',
        requestMethod: POST,
        schema: null,
        payload: { token: awsMktplaceToken },
        ignoreErrors: true,
      },
    });
  };

export const authWithExternalToken = (token) => (dispatch) => {
  if (!token) {
    return Promise.reject(new Error('No external token provided'));
  }

  dispatch(clearSessionError());
  return dispatch({
    [CALL_AUTH_API]: {
      types: [
        actionTypes.NEX_EXTERNAL_TOKEN_AUTH_REQUEST,
        actionTypes.NEX_EXTERNAL_TOKEN_AUTH_SUCCESS,
        actionTypes.NEX_EXTERNAL_TOKEN_AUTH_FAILURE,
      ],
      endpoint: '/token/refresh',
      requestMethod: POST,
      schema: sessionSchema,
      payload: {
        accessToken: token,
      },
      ignoreErrors: true,
    },
  })
    .then(() => {
      dispatch(loadUser());
      loadInitialUserData(dispatch);
    })
    .catch((error) => {
      LocalStorageUtils.removeObject('nexSession');
      LocalStorageUtils.removeObject('nexla_section_navigation');
      dispatch({ type: actionTypes.LOGOUT_COMPLETE });
      throw error instanceof Error ? error : new Error('Authentication failed');
    });
};

export const switchToOrg = (org_id) => (dispatch, getState) => {
  const state = getState();
  const accessToken = getAccessToken(state);
  const user = getCurrentUser(state);
  const orgs = user.org_memberships;
  localStorage.setItem(`nex_org_id`, org_id);
  if (orgs) {
    const org = orgs.find((o) => o.id === org_id);
    if (org) {
      localStorage.setItem(`nex_org_name`, org.name);
    }
  }

  LocalStorageUtils.removeObject('nexla_section_navigation');
  dispatch({ type: actionTypes.LOGOUT_COMPLETE });

  return dispatch({
    [CALL_AUTH_API]: {
      types: [
        actionTypes.NEX_ORG_SWITCH_REQUEST,
        actionTypes.NEX_ORG_SWITCH_SUCCESS,
        actionTypes.NEX_ORG_SWITCH_FAILURE,
      ],
      endpoint: `/token/refresh`,
      schema: sessionSchema,
      requestMethod: POST,
      payload: {
        org_id,
        accessToken,
      },
    },
  }).then(() => {
    dispatch(loadVendors(true));
    //TODO: NEX-13948: Temp hack for fetching org. We need it to know features enabled for the org

    dispatch(fetchCurrentOrg());
    dispatch(getAllTags(true));
    dispatch(getUserSettings(true));
    dispatch(loadUnreadNotifications());
  });
};

// Validates that the current access token is still accepted by the server.
// ignoreErrors: true — a network hiccup or 5xx on tab focus should not show a
// global error toast; 401 is handled automatically by the CALL_PRIVATE_API middleware.
export const validateCurrentSession = () => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.VALIDATE_SESSION_REQUEST,
        actionTypes.VALIDATE_SESSION_SUCCESS,
        actionTypes.VALIDATE_SESSION_FAILURE,
      ],
      endpoint: '/users/current',
      requestMethod: GET,
      schema: null,
      ignoreErrors: true,
    },
  });
};
